import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  Card,
  Tabs,
  Typography,
  Button,
} from "antd";
import moment from "moment";
import { getDirectusClient } from "../../../lib/directus";
import { readItems } from "@directus/sdk";
import { useSelector } from "react-redux";
import { formatPrice } from "../../../lib/utils";

const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

export default function LiquidacionDetailsModal({
  visible,
  liquidacion,
  cajas,
  onClose,
}) {
  const signoMoneda = useSelector((state) => state.signoMoneda.signo_moneda);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [consignaciones, setConsignaciones] = useState([]);
  const [loadingConsignaciones, setLoadingConsignaciones] = useState(false);
  
  // Estados para el visor de PDF
  const [pdfVisible, setPdfVisible] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  const fetchFacturas = async () => {
    if (!liquidacion?.cajas?.length) return;

    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const cajasIds = Array.isArray(liquidacion.cajas)
        ? liquidacion.cajas.map((c) => c.cajas_id)
        : [];
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "id",
            "consecutivo",
            "estado",
            "tipo",
            "date_created",
            "productos.cantidad",
            "productos.precio_unitario",
            "productos.producto.grupo.impuesto",
            "caja.vendedor.first_name",
            "caja.vendedor.last_name",
          ],
          filter: {
            caja: { _in: cajasIds },
          },
        })
      );
      setFacturas(response);
    } catch (error) {
      console.error("Error fetching facturas:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch consignaciones
  const fetchConsignaciones = async () => {
    if (!liquidacion?.id) return;
    setLoadingConsignaciones(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("consignaciones", {
          fields: ["monto", "banco.nombre", "evidencia"],
          filter: {
            _and: [
              { liquidacion: { id: { _eq: liquidacion.id } } }
            ]
          }
        })
      );
      setConsignaciones(response);
    } catch (error) {
      console.error("Error fetching consignaciones:", error);
    } finally {
      setLoadingConsignaciones(false);
    }
  };

  useEffect(() => {
    if (visible && liquidacion) {
      fetchFacturas();
      fetchConsignaciones();
    }
  }, [visible, liquidacion]);

  const cajasColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Vendedor",
      key: "vendedor",
      render: (_, caja) =>
        `${caja.vendedor?.first_name || ""} ${caja.vendedor?.last_name || ""}`,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => {
        const color = estado === 1 ? "green" : "red";
        const text = estado === 1 ? "ABIERTA" : "CERRADA";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Liquidada",
      dataIndex: "liquidada",
      key: "liquidada",
      render: (liquidada) => (
        <Tag color={liquidada ? "green" : "orange"}>
          {liquidada ? "SÍ" : "NO"}
        </Tag>
      ),
    },
    {
      title: "Fecha Apertura",
      dataIndex: "fecha_apertura",
      key: "fecha_apertura",
      render: (fecha) => moment(fecha).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Fecha Cierre",
      dataIndex: "fecha_cierre",
      key: "fecha_cierre",
      render: (fecha) =>
        fecha ? moment(fecha).format("DD/MM/YYYY HH:mm") : "-",
    },
  ];

  const facturasColumns = [
    {
      title: "Consecutivo",
      dataIndex: "consecutivo",
      key: "consecutivo",
    },
    {
      title: "Vendedor",
      key: "vendedor",
      render: (_, record) =>
        `${record.caja?.vendedor?.first_name || ""} ${
          record.caja?.vendedor?.last_name || ""
        }`,
    },
    {
      title: "Fecha",
      dataIndex: "date_created",
      key: "date_created",
      render: (fecha) => moment(fecha).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => (
        <Tag color={estado === "creada" ? "green" : "red"}>
          {estado?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Total",
      key: "total",
      render: (_, record) => {
        const total = (record.productos || []).reduce((sum, prod) => {
          const subtotal = (prod.cantidad || 0) * (prod.precio_unitario || 0);
          const impuesto = prod.producto?.grupo?.impuesto || 0;
          const impuestoValue = subtotal * (impuesto / 100);
          return sum + (subtotal + impuestoValue);
        }, 0);
        return formatPrice(total,signoMoneda);
      },
    },
  ];

  return (
    <Modal
      title="Detalles de Liquidación"
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
    >
      <Tabs defaultActiveKey="liquidacion">
        <TabPane tab="Liquidación" key="liquidacion">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Facturas"
                  value={formatPrice(liquidacion?.total_facturas,signoMoneda)|| 0}
                
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Crédito"
                  value={formatPrice(liquidacion?.total_credito,signoMoneda)|| 0}
                
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Diferencia"
                  value={formatPrice(liquidacion?.diferencia,signoMoneda) || 0}
                 
                  valueStyle={{
                    color: liquidacion?.diferencia < 0 ? "#cf1322" : "#3f8600",
                  }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Devoluciones"
                  value={formatPrice(liquidacion?.total_devoluciones,signoMoneda) || 0}
                 
                />
              </Card>
            </Col>
          </Row>

         
          {consignaciones.length > 0 && (
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card title="Consignaciones" loading={loadingConsignaciones}>
                  <div>
                    <strong>Total consignado: </strong>
                    {formatPrice(consignaciones.reduce((sum, c) => sum + (parseFloat(c.monto) || 0), 0), signoMoneda)}
                  </div>
                  <ul style={{ marginTop: 12, paddingLeft: 20 }}>
                    {consignaciones.map((c, idx) => (
                      <li key={idx} style={{ marginBottom: 8 }}>
                        <strong>Banco:</strong> {c.banco?.nombre || "-"} &nbsp;|&nbsp;
                        <strong>Monto:</strong> {formatPrice(parseFloat(c.monto), signoMoneda)}
                        {c.evidencia && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => {
                              setSelectedEvidence(c.evidencia);
                              setPdfVisible(true);
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            Ver Evidencia
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            </Row>
          )}

          {liquidacion?.novedades && (
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card title="Novedad">
                  <Row>
                    <Col span={24}>
                      <div
                        style={{
                          backgroundColor: "#fff2e8",
                          padding: 16,
                          borderRadius: 4,
                          border: "1px solid #ffbb96",
                        }}
                      >
                        <Text type="warning" strong>
                          Motivo de la diferencia:
                        </Text>
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                          {liquidacion.novedades}
                        </Paragraph>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          {liquidacion?.aceptada && (
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card title="Información de Aceptación">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Fecha de Aceptación"
                        value={moment(liquidacion.fecha_aceptacion).format(
                          "DD/MM/YYYY HH:mm"
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Aceptada por"
                        value={liquidacion.aceptada_por?.first_name || ""}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </TabPane>

        <TabPane tab="Cajas" key="cajas">
          <Table
            columns={cajasColumns}
            dataSource={cajas}
            pagination={false}
            rowKey="id"
          />
        </TabPane>

        <TabPane tab="Facturas" key="facturas">
          <Table
            columns={facturasColumns}
            dataSource={facturas}
            loading={loading}
            pagination={false}
            rowKey="id"
          />
        </TabPane>
      </Tabs>
      
      {/* Modal para mostrar la evidencia PDF */}
      {pdfVisible && selectedEvidence && (
        <Modal
          title="Evidencia de Consignación"
          open={pdfVisible}
          onCancel={() => {
            setPdfVisible(false);
            setSelectedEvidence(null);
          }}
          footer={null}
          width={800}
          style={{ top: 20 }}
        >
          <iframe
            src={`${process.env.REACT_APP_URL_DIRECTUS}/assets/${selectedEvidence}`}
            style={{
              width: '100%',
              height: '80vh',
              border: 'none'
            }}
            title="PDF Viewer"
          />
        </Modal>
      )}
    </Modal>
  );
}
